import React, { act, useState } from "react";
import { IoMdEye } from "react-icons/io";
import { MdOutlineEdit } from "react-icons/md";
import { RiDeleteBin6Line } from "react-icons/ri";
import { Link, useParams } from "react-router-dom";
import { getHotelById } from "../../../../api/owner/hotels";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  getActivitiesByHotelId,
  addActivity,
  updateActivity,
  deleteActivity,
} from "../../../../api/owner/hotels/activities";
import { updateHotel } from "../../../../api/owner/hotels";
import toast from "react-hot-toast";
import Swal from "sweetalert2";
import Pagination from "../../../../components/common/Pagination";
import AddHotelModel from "../../../../components/ui/AddHotelModel";
import { useTranslation } from "../../../../hooks/useTranslation";

const PopupModal = ({ isOpen, onClose, children }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed sm:inset-0 bottom-0 left-0 right-0 bg-black bg-opacity-50 flex sm:items-center items-end justify-center z-50 h-full">
      <div className="bg-white sm:rounded-[20px] rounded-t-[20px] w-full sm:w-[845px] relative sm:shadow-lg">
        <button
          onClick={onClose}
          className="absolute top-[33px] right-[30px] text-[#9E9E9E] hover:text-black"
        >
          ✕
        </button>
        {children}
      </div>
    </div>
  );
};

const UserHotelDetail = () => {
  const { t } = useTranslation();
  const [enabled, setEnabled] = useState(true);
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const queryClient = useQueryClient();
  const { hotelId } = useParams();
  const [isEditing, setIsEditing] = useState(false);
  const [isViewing, setIsViewing] = useState(false);
  const [isAddHotelPopupOpen, setIsAddHotelPopupOpen] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    category: "",
    price: "",
    duration: "",
    slots: "",
    description: "",
    hotelId: hotelId,
    activityId: "",
  });
  const [page, setPage] = useState(1);
  const limit = 5;

  const [errors, setErrors] = useState({
    name: "",
    category: "",
    price: "",
    duration: "",
    slots: "",
    description: "",
    activityId: "",
  });

  const { data, isLoading } = useQuery({
    queryKey: ["hotel", hotelId],
    queryFn: () => getHotelById(hotelId),
    enabled: !!hotelId,
  });

  const { data: activitiesData, isLoading: activitiesLoading } = useQuery({
    queryKey: ["activities", hotelId, page],
    queryFn: () => getActivitiesByHotelId({ page, limit, hotelId }),
    enabled: !!hotelId,
  });

  const hotel = data?.data;
  const activities = activitiesData?.data?.activities || [];
  const pagination = activitiesData?.data?.pagination || {};

  const addActivityMutation = useMutation({
    mutationFn: (newActivity) => addActivity(newActivity),
    onSuccess: () => {
      queryClient.invalidateQueries(["activities", hotelId]);
      closeModal();
      toast.success(t("userHotelDetail").toastMessages.activityAdded);
    },
    onError: (error) => {
      const errorMessage =
        error?.response?.data?.message ||
        t("userHotelDetail").toastMessages.addActivityError;
      toast.error(errorMessage);
    },
  });

  const updateActivityMutation = useMutation({
    mutationFn: ({ activityId, updatedActivity }) =>
      updateActivity(activityId, updatedActivity),
    onSuccess: () => {
      queryClient.invalidateQueries(["activities", hotelId]);
      closeModal();
      toast.success(t("userHotelDetail").toastMessages.activityUpdated);
    },
    onError: (error) => {
      const errorMessage =
        error?.response?.data?.message ||
        t("userHotelDetail").toastMessages.updateActivityError;
      toast.error(errorMessage);
    },
  });

  const updateHotelMutation = useMutation({
    mutationFn: (hotelData) => updateHotel(hotelId, hotelData),
    onSuccess: () => {
      queryClient.invalidateQueries(["hotel", hotelId]);
      queryClient.invalidateQueries(["hotels"]);
      toast.success(t("userHotelDetail").toastMessages.hotelUpdated);
      closeModal();
    },
    onError: (error) => {
      const errorMessage =
        error?.response?.data?.message ||
        t("userHotelDetail").toastMessages.updateError;
      toast.error(errorMessage);
    },
  });

  const deleteActivityMutation = useMutation({
    mutationFn: (activityId) => deleteActivity(activityId),
    onSuccess: () => {
      queryClient.invalidateQueries(["activities", hotelId]);
    },
  });

  const validateField = (fieldName, value) => {
    const updatedFormData = { ...formData, [fieldName]: value };
    const newErrors = validateForm(updatedFormData);
    setErrors((prevErrors) => ({
      ...prevErrors,
      [fieldName]: newErrors[fieldName] || "",
    }));
  };

  const validateForm = (data) => {
    const newErrors = {};
    const today = new Date();
    const startDate = new Date(data.startDate);
    const endDate = new Date(data.endDate);

    if (!data.name)
      newErrors.name = t("userHotelDetail").validation.nameRequired;
    if (!data.description)
      newErrors.description = t("userHotelDetail").validation.descriptionRequired;
    if (!data.category)
      newErrors.category = t("userHotelDetail").validation.categoryRequired;
    if (!data.price || isNaN(data.price))
      newErrors.price = t("userHotelDetail").validation.priceRequired;
    if (!data.duration || isNaN(data.duration))
      newErrors.duration = t("userHotelDetail").validation.durationRequired;
    if (data.duration && (data.duration <= 0 || data.duration > 24))
      newErrors.duration = t("userHotelDetail").validation.durationRange;
    if (!data.slots || isNaN(data.slots))
      newErrors.slots = t("userHotelDetail").validation.slotsRequired;

    return newErrors;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));

    validateField(name, value);
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    const newErrors = validateForm(formData);
    setErrors(newErrors);

    if (Object.keys(newErrors).length === 0) {
      if (isEditing) {
        updateActivityMutation.mutate({
          activityId: formData.activityId,
          updatedActivity: {
            name: formData.name,
            category: formData.category,
            price: parseFloat(formData.price),
            duration: parseFloat(formData.duration),
            slots: parseInt(formData.slots),
            hotelId: formData.hotelId,
            description: formData.description,
          },
        });
      } else {
        addActivityMutation.mutate({
          name: formData.name,
          category: formData.category,
          price: parseFloat(formData.price),
          duration: parseFloat(formData.duration),
          slots: parseInt(formData.slots),
          hotelId: formData.hotelId,
          description: formData.description,
        });
      }
    }
  };

  const closeModal = () => {
    setIsPopupOpen(false);
    setIsEditing(false);
    setIsViewing(false);
    setFormData({
      name: "",
      category: "",
      price: "",
      duration: "",
      slots: "",
      description: "",
      hotelId: hotelId,
      endDate: "",
    });
    setErrors({
      name: "",
      category: "",
      price: "",
      duration: "",
      slots: "",
      description: "",
      endDate: "",
    });
  };

  const handleDeleteActivity = (activityId) => {
    Swal.fire({
      title: t("userHotelDetail").deleteConfirm.title,
      text: t("userHotelDetail").deleteConfirm.text,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: t("userHotelDetail").deleteConfirm.confirm,
      cancelButtonText: t("userHotelDetail").deleteConfirm.cancel,
    }).then((result) => {
      if (result.isConfirmed) {
        deleteActivityMutation.mutate(activityId, {
          onSuccess: () => {
            toast.success(t("userHotelDetail").toastMessages.activityDeleted);
          },
          onError: (error) => {
            const errorMessage =
              error?.response?.data?.message ||
              t("userHotelDetail").toastMessages.deleteActivityError;
            toast.error(errorMessage);
          },
        });
      }
    });
  };

  const handleToggle = (currentValue) => {
    if (activities.length === 0) {
      toast.error(t("userHotelDetail").toastMessages.addActivityFirst);
      return;
    }
    updateHotelMutation.mutate({
      hotelId,
      activityBookingEnabled: !currentValue,
    });
  };

  const handlePayAtCheckoutToggle = (currentValue) => {
    updateHotelMutation.mutate({
      hotelId,
      payAtCheckout: !currentValue,
    });
  };

  const openModel = () => {
    if (hotel?.verificationStatus !== "approved") {
      toast.error(t("userHotelDetail").toastMessages.hotelNotApproved);
      return;
    }
    setIsPopupOpen(true);
  };

  if (isLoading || activitiesLoading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="w-12 h-12 border-4 border-green-500 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <>
      <div>
        <h2 className="md:text-[28px] text-[22px] font-semibold text-black">
          {t("userHotelDetail").title}
        </h2>
        <div className="md:mt-[30px] mt-[20px] grid grid-cols-1 lg:grid-cols-[417px_1fr] gap-[20px]">
          <div className="border border-[#DDDDDD] py-[30px] px-[20px] rounded-[10px]  bg-white">

            {/* Show pending approval message */}
            {hotel?.verificationStatus === "pending" &&(
              <div className="mb-4 p-4 bg-yellow-100 border-l-4 border-yellow-500 text-yellow-800 rounded">
                {t("userHotelDetail").pendingMessage}
              </div>
            )}

            <div className="flex justify-between items-center mb-[25px]">
              <h2 className="text-[22px] font-medium">
                {t("userHotelDetail").basicInfo.title}
              </h2>
              <button onClick={() => setIsAddHotelPopupOpen(true)}>
                <MdOutlineEdit size={24} />
              </button>
            </div>
            <div>
              <ul className="">
                <li className="flex items-center mb-[20px] gap-[10px] text-lg text-black">
                  <span className="font-medium">
                    {t("userHotelDetail").basicInfo.name}
                  </span>
                  {hotel?.name}
                </li>
                <li className="flex items-center mb-[20px] gap-[10px] text-lg text-black">
                  <span className="font-medium">
                    {t("userHotelDetail").basicInfo.category}
                  </span>
                  {hotel?.category}
                </li>
                <li className="flex items-center mb-[20px] gap-[10px] text-lg text-black">
                  <span className="font-medium">
                    {t("userHotelDetail").basicInfo.starRating}
                  </span>
                  {hotel?.rating}
                </li>
                <li className="flex items-center mb-[20px] gap-[10px] text-lg text-black">
                  <span className="font-medium">
                    {t("userHotelDetail").basicInfo.owner}
                  </span>
                  {hotel?.ownerId?.name}
                </li>

                <li className="flex items-center mb-[20px] gap-[10px] text-lg text-black">
                  <span className="font-medium">
                    {t("userHotelDetail").basicInfo.payAtCheckout}
                  </span>
                  <div
                    className={`w-[40px] h-[20px] flex items-center rounded-full p-1 transition-colors ${hotel.payAtCheckout ? "bg-[#4CAF50]" : "bg-[#e1e1e1]"
                      }`}
                  >
                    <div
                      onClick={() =>
                        handlePayAtCheckoutToggle(hotel?.payAtCheckout)
                      }
                      className={`bg-white w-[16px] h-[16px] rounded-full shadow-md transform transition-transform ${hotel.payAtCheckout ? "translate-x-4" : "translate-x-0"
                        }`}
                    />
                  </div>
                </li>
              </ul>
            </div>
          </div>
          <div className="border border-[#DDDDDD] py-[30px] px-[25px] rounded-[10px]  bg-white">
            <div className="flex justify-between gap-[10px] items-center flex-wrap sm:mb-0 mb-[20px]">
              <h2 className="text-[22px] font-medium text-black">
                {t("userHotelDetail").activityBookings.title}
              </h2>
              <button
                onClick={openModel}
                className="inline-block w-full sm:max-w-[205px] rounded-[2px] bg-[#4CAF50] 2xl:text-[20px] text-base text-white text-center font-semibold 2xl:p-[9px] p-3 green-shadow hover:bg-[#209b25]"
              >
                {t("userHotelDetail").activityBookings.addButton}
              </button>
            </div>
            <div className="flex gap-[28px] items-center">
              <p className="text-lg">
                {t("userHotelDetail").activityBookings.enable}
              </p>
              <div
                onClick={() => handleToggle(hotel?.activityBookingEnabled)}
                className={`w-[40px] h-[20px] flex items-center rounded-full p-1 cursor-pointer transition-colors ${hotel?.activityBookingEnabled
                  ? "bg-[#4CAF50]"
                  : "bg-[#e1e1e1]"
                  }`}
              >
                <div
                  className={`bg-white w-[16px] h-[16px] rounded-full shadow-md transform transition-transform ${hotel?.activityBookingEnabled
                    ? "translate-x-4"
                    : "translate-x-0"
                    }`}
                />
              </div>
            </div>
            <div className="  overflow-hidden mt-[25px]">
              <div className="overflow-x-auto grid">
                <table className="min-w-full bg-white ">
                  <thead className="bg-[#FFFAF2] ">
                    <tr className="">
                      <th className="text-left text-lg font-medium text-[#000000] py-[22px] px-[20px] whitespace-nowrap">
                        {
                          t("userHotelDetail").activityBookings.tableHeaders
                            .activityName
                        }
                      </th>
                      <th className="text-left text-lg font-medium text-[#000000] py-[22px] px-[20px] whitespace-nowrap">
                        {
                          t("userHotelDetail").activityBookings.tableHeaders
                            .category
                        }
                      </th>
                      <th className="text-left text-lg font-medium text-[#000000] py-[22px] px-[20px] whitespace-nowrap">
                        {
                          t("userHotelDetail").activityBookings.tableHeaders
                            .price
                        }
                      </th>
                      <th className="text-left text-lg font-medium text-[#000000] py-[22px] px-[20px] whitespace-nowrap">
                        {
                          t("userHotelDetail").activityBookings.tableHeaders
                            .duration
                        }
                      </th>

                      <th className="text-left text-lg font-medium text-[#000000] py-[22px] px-[20px] whitespace-nowrap">
                        {
                          t("userHotelDetail").activityBookings.tableHeaders
                            .availableSlots
                        }
                      </th>
                      <th className="text-left text-lg font-medium text-[#000000] py-[22px] px-[20px] whitespace-nowrap">
                        {
                          t("userHotelDetail").activityBookings.tableHeaders
                            .action
                        }
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {activities.length > 0 ? (
                      activities.map((activity) => (
                        <tr
                          key={activity._id}
                          className="border-t border-[#E4E4E4] "
                        >
                          <td className="py-[26px] px-[20px] text-base font-normal text-black">
                            {activity.name}
                          </td>
                          <td className="py-[16px] px-[32px] text-base font-normal text-black  whitespace-nowrap">
                            {activity.category}
                          </td>
                          <td className="py-[16px] px-[32px] text-base font-normal text-black  whitespace-nowrap">
                            ${activity.price}
                          </td>
                          <td className="py-[16px] px-[32px] text-base font-normal text-black  whitespace-nowrap">
                            {activity.duration} hr
                          </td>
                          <td className="py-[16px] px-[32px] text-base font-normal text-black  whitespace-nowrap">
                            {activity.slots} slots
                          </td>
                          <td className="py-[16px] px-[32px] flex gap-[12px]">
                            <button
                              onClick={() => {
                                setFormData({
                                  name: activity.name,
                                  category: activity.category,
                                  price: activity.price,
                                  duration: activity.duration,
                                  slots: activity.slots,
                                  hotelId: hotelId,
                                });
                                setIsPopupOpen(true);
                                setIsViewing(true);
                              }}
                              className="text-black font-normal w-[32px] h-[32px] flex items-center justify-center rounded-full bg-[#F2F2F2] "
                            >
                              <IoMdEye className="text-base text-black" />
                            </button>
                            <button
                              onClick={() => {
                                setFormData({
                                  name: activity.name,
                                  category: activity.category,
                                  price: activity.price,
                                  duration: activity.duration,
                                  slots: activity.slots,
                                  hotelId: hotelId,
                                  activityId: activity._id,
                                  description: activity.description,
                                });
                                setIsEditing(true);
                                setIsPopupOpen(true);
                              }}
                              className="text-black font-normal w-[32px] h-[32px] flex items-center justify-center rounded-full bg-[#F2F2F2] "
                            >
                              <MdOutlineEdit className="text-base text-black" />
                            </button>
                            <button
                              onClick={() => {
                                handleDeleteActivity(activity._id);
                              }}
                              className="text-black font-normal w-[32px] h-[32px] flex items-center justify-center rounded-full bg-[#F2F2F2] "
                            >
                              <RiDeleteBin6Line className="text-base text-black" />
                            </button>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr className="border-t border-[#E4E4E4] ">
                        <td
                          colSpan="6"
                          className="py-[26px] px-[20px] text-base font-normal text-black text-center"
                        >
                          {t("userHotelDetail").activityBookings.noActivities}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
              {activities.length > 0 && (
                <Pagination
                  page={pagination.page}
                  totalPages={pagination.totalPages}
                  setPage={setPage}
                />
              )}
            </div>
          </div>
        </div>
      </div>
      {/* Modal Popup */}
      <PopupModal isOpen={isPopupOpen} onClose={closeModal}>
        <div className="w-full rounded-[20px] p-[30px]">
          <h2 className="text-[20px] font-medium pb-5 border-b border-[#EEEEEE] mb-[19px]">
            {isEditing
              ? t("userHotelDetail").modal.editActivity
              : t("userHotelDetail").modal.newActivity}
          </h2>
          <div className="grid grid-col-1 sm:grid-cols-2 gap-[16px] mb-[19px]">
            <div className="w-full">
              <label className="text-sm text-[#000000] mb-[8px] block">
                {t("userHotelDetail").modal.labels.activityName}
              </label>
              <input
                type="text"
                name="name"
                disabled={isViewing}
                value={formData.name}
                onChange={handleInputChange}
                placeholder={t("userHotelDetail").modal.labels.activityName}
                className={`border rounded-[10px] py-[13px] px-[16px] w-full focus:outline-none ${errors.name ? "border-red-500" : "border-[#DDDDDD]"
                  }`}
              />
              {errors.name && (
                <p className="text-red-500 text-sm mt-1">{errors.name}</p>
              )}
            </div>
            <div className="w-full">
              <label className="text-sm text-[#000000] mb-[8px] block">
                {t("userHotelDetail").modal.labels.activityDescription}
              </label>
              <input
                type="text"
                name="description"
                disabled={isViewing}
                value={formData.description}
                onChange={handleInputChange}
                placeholder={
                  t("userHotelDetail").modal.labels.activityDescription
                }
                className={`border rounded-[10px] py-[13px] px-[16px] w-full focus:outline-none ${errors.description ? "border-red-500" : "border-[#DDDDDD]"
                  }`}
              />
              {errors.description && (
                <p className="text-red-500 text-sm mt-1">
                  {errors.description}
                </p>
              )}
            </div>
            <div className="w-full">
              <label className="text-sm text-[#000000] mb-[8px] block">
                {t("userHotelDetail").modal.labels.category}
              </label>
              <select
                name="category"
                disabled={isViewing}
                value={formData.category}
                onChange={handleInputChange}
                className={`border rounded-[10px] py-[13px] px-[16px] w-full focus:outline-none ${errors.category ? "border-red-500" : "border-[#DDDDDD]"
                  }`}
              >
                <option value="" disabled>
                  {t("userHotelDetail").modal.labels.category}
                </option>
                <option value="Spa Massage">
                  {t("userHotelDetail").modal.labels.options.SpaMassage}
                </option>
                <option value="Horse Riding">
                  {t("userHotelDetail").modal.labels.options.HorseRiding}
                </option>
                <option value="Swimming">
                  {t("userHotelDetail").modal.labels.options.Swimming}
                </option>
                <option value="Yoga">
                  {t("userHotelDetail").modal.labels.options.Yoga}
                </option>
                <option value="Hiking">
                  {t("userHotelDetail").modal.labels.options.Hiking}
                </option>
                <option value="Cycling">
                  {t("userHotelDetail").modal.labels.options.Cycling}
                </option>
                <option value="Snorkeling">
                  {t("userHotelDetail").modal.labels.options.Snorkeling}
                </option>
                <option value="City Tour">
                  {t("userHotelDetail").modal.labels.options.CityTour}
                </option>
                <option value="Cooking Class">
                  {t("userHotelDetail").modal.labels.options.CookingClass}
                </option>
                <option value="Wine Tasting">
                  {t("userHotelDetail").modal.labels.options.WineTasting}
                </option>
              </select>
              {errors.category && (
                <p className="text-red-500 text-sm mt-1">{errors.category}</p>
              )}
            </div>
          </div>
          <div className="w-full mb-[19px]">
            <label className="text-sm text-[#000000] mb-[8px] block">
              {t("userHotelDetail").modal.labels.price}
            </label>
            <input
              type="text"
              name="price"
              disabled={isViewing}
              value={formData.price}
              onChange={handleInputChange}
              placeholder={t("userHotelDetail").modal.placeholders.price}
              className={`border rounded-[10px] py-[13px] px-[16px] w-full focus:outline-none ${errors.price ? "border-red-500" : "border-[#DDDDDD]"
                }`}
            />
            {errors.price && (
              <p className="text-red-500 text-sm mt-1">{errors.price}</p>
            )}
          </div>

          <div className="grid-col-1 sm:grid-cols-2 gap-[16px] mb-[19px]">
            <div className="w-full">
              <label className="text-sm text-[#000000] mb-[8px] block">
                {t("userHotelDetail").modal.labels.duration}
              </label>
              <input
                type="text"
                name="duration"
                value={formData.duration}
                onChange={handleInputChange}
                placeholder={t("userHotelDetail").modal.placeholders.duration}
                className={`border rounded-[10px] py-[13px] px-[16px] w-full focus:outline-none ${errors.duration ? "border-red-500" : "border-[#DDDDDD]"
                  }`}
              />
              {errors.duration && (
                <p className="text-red-500 text-sm mt-1">{errors.duration}</p>
              )}
            </div>
          </div>

          <div className="w-full mb-3">
            <label className="text-sm text-[#000000] mb-[8px] block">
              {t("userHotelDetail").modal.labels.availableSlots}
            </label>
            <input
              type="text"
              name="slots"
              disabled={isViewing}
              value={formData.slots}
              onChange={handleInputChange}
              placeholder={
                t("userHotelDetail").modal.placeholders.availableSlots
              }
              className={`border rounded-[10px] py-[13px] px-[16px] w-full focus:outline-none ${errors.slots ? "border-red-500" : "border-[#DDDDDD]"
                }`}
            />
            {errors.slots && (
              <p className="text-red-500 text-sm mt-1">{errors.slots}</p>
            )}
          </div>

          {!isViewing && (
            <div className="flex gap-[30px] justify-end pt-[23px] mt-[30px] border-t border-[#EEEEEE]">
              <Link
                to="#"
                onClick={closeModal}
                className="text-white font-bold inline-block py-[14px] px-[31px] rounded-[2px] bg-[#CCCCCC] hover:bg-[#B8B8B8] text-[20px] text-center gray-shadow"
              >
                {t("userHotelDetail").modal.buttons.cancel}
              </Link>
              <Link
                to="#"
                onClick={handleSubmit}
                disabled={addActivityMutation.isPending || updateActivityMutation.isPending}
                className="inline-block rounded-[2px] bg-[#4CAF50] 2xl:text-[20px] text-base text-white text-center font-bold py-[14px] px-[30px] green-shadow hover:bg-[#209b25]"
              >
                {(addActivityMutation.isPending || updateActivityMutation.isPending) ? t("header").loading : isEditing
                  ? t("userHotelDetail").modal.buttons.update
                  : t("userHotelDetail").modal.buttons.add}
              </Link>
            </div>
          )}
        </div>
      </PopupModal>

      <PopupModal
        isOpen={isAddHotelPopupOpen}
        onClose={() => setIsAddHotelPopupOpen(false)}
      >
        <AddHotelModel
          isOpen={isAddHotelPopupOpen}
          hotel={hotel}
          isEditing={true}
          onClose={() => setIsAddHotelPopupOpen(false)}
        />
      </PopupModal>
    </>
  );
};

export default UserHotelDetail;
